import React, { useEffect } from "react";
import { X } from "lucide-react";
import { getImageSrc } from "components/Tableimageshelpers";
import "../assets/css/SignageOrders.css";

/** Minimal vendor shape used by VendorsPage, Signagesuppliersbrands, SignageSuppliersCategory, AgentSupplierPage */
export type VendorDetailsProfile = {
  _id?: string;
  user_id?: string;
  vendor_name: string;
  tag_line?: string;
  description?: string;
  about_company?: string;
  category?: string;
  verified?: boolean;
  partner?: boolean;
  logo?: string;
  core_specialties?: string[];
  Service?: string;
  contact_info?: {
    website?: string;
    email?: string;
    phone?: string;
    location?: { label?: string };
  };
  business_details?: {
    year_of_established?: number;
    price_model?: string;
  };
};

export type VendorDetailsDiscount = {
  _id: string;
  code: string;
  type: string;
  value: number;
};

export type VendorDetailsReview = {
  _id: string;
  user_name?: string;
  created_at?: string;
  rating?: number;
  review_title?: string;
  review_comment?: string;
  recommend_vendor?: boolean;
};

export type VendorDetailsSliderProps = {
  show: boolean;
  onClose: () => void;
  profile: VendorDetailsProfile | null;
  /** Optional list-item fallback when profile is from list (e.g. VendorsPage card click) */
  listItem?: VendorDetailsProfile | null;
  logoUrlMap?: Record<string, string>;
  loading?: boolean;
  detailsError?: string | null;
  avgRating?: number;
  reviewCount?: number;
  reviews?: VendorDetailsReview[];
  loadingReviews?: boolean;
  reviewsError?: string | null;
  discounts?: VendorDetailsDiscount[];
  onCopyCouponCode?: (code: string, el: HTMLElement | null) => void;
  onConnect?: () => void;
  onWriteReview?: () => void;
  isLinked?: boolean;
  loadingLinked?: boolean;
};

const VendorDetailsSlider: React.FC<VendorDetailsSliderProps> = ({
  show,
  onClose,
  profile,
  listItem,
  logoUrlMap = {},
  loading = false,
  detailsError = null,
  avgRating,
  reviewCount = 0,
  reviews = [],
  loadingReviews = false,
  reviewsError = null,
  discounts = [],
  onCopyCouponCode,
  onConnect,
  onWriteReview,
  isLinked = false,
  loadingLinked = false,
}) => {
  useEffect(() => {
    if (show) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const onEscape = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [show, onClose]);

  if (!show) return null;

  const v = profile || listItem;
  const name = v?.vendor_name || "Vendor";
  const tagline = v?.tag_line || "";
  const service = (v as any)?.Service || v?.category || "N/A";
  const rating = avgRating ?? 4.7;
  const count = reviewCount ?? 0;
  const idStr = (v as any)?._id ?? "";
  const quality = 4.5 + (idStr ? (idStr.charCodeAt(0) % 5) / 10 : 0.2);
  const communication = 4.3 + (idStr ? (idStr.charCodeAt(1) % 5) / 10 : 0.3);
  const timeliness = 4.4 + (idStr ? (idStr.charCodeAt(2) % 5) / 10 : 0.2);
  const value = 4.2 + (idStr ? (idStr.charCodeAt(3) % 5) / 10 : 0.3);
  const profileData = profile;
  const website = profileData?.contact_info?.website || `https://${name.toLowerCase().replace(/[^a-z0-9]/g, "")}.com`;
  const phone = profileData?.contact_info?.phone || "(555) 123-4567";
  const email = profileData?.contact_info?.email || `contact@${name.toLowerCase().replace(/[^a-z0-9]/g, "")}.com`;
  const location = profileData?.contact_info?.location?.label || "123 Williams Street, New York, NY 10038";
  const aboutText = profileData?.about_company || profileData?.description || `With over 20 years of experience in ${service.toLowerCase()}, ${name} specializes in creating stunning visual content that sells properties.`;
  const specialties = profileData?.core_specialties || (v as any)?.core_specialties || [service];
  const secondaryService = specialties[1] || "N/A";
  const badgeClass = v?.verified ? "verified" : v?.partner ? "partner" : "new";
  const badgeText = v?.verified ? "Verified" : v?.partner ? "Partner" : "New";
  const logoUrl = v?.logo ? (getImageSrc(v.logo, logoUrlMap) || (v.logo.startsWith("http") ? v.logo : `${process.env.REACT_APP_BASE_URL || ""}${v.logo}`)) : "";

  return (
    <>
      <div
        className={`details-overlay ${show ? "active" : ""}`}
        onClick={onClose}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close vendor details"
      />
      <div
        className={`details-panel ${show ? "active" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Vendor details"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="details-panel-header">
          <span className="details-panel-title">Vendor Details</span>
          <button
            type="button"
            className="details-close-btn"
            onClick={onClose}
            aria-label="Close"
            style={{ width: 30, height: 30, minWidth: 54, minHeight: 54, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
          >
            <X size={50} style={{ color: "black", width: 32, height: 50 }} />
          </button>
        </div>

        <div className="details-panel-content">
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
              <p className="mt-2 text-muted">Loading...</p>
            </div>
          )}
          {detailsError && (
            <div className="alert alert-danger" role="alert">{detailsError}</div>
          )}
          {!loading && !detailsError && v && (
            <>
              <div className="details-header">
                <div className="details-header-top">
                  <div className="details-header-left">
                    <div className={`details-vendor-logo ${!logoUrl ? "logo-1" : ""}`}>
                      {logoUrl ? (
                        <img src={logoUrl} alt={name} style={{ width: 72, height: 72, objectFit: "contain", borderRadius: 12 }} />
                      ) : (
                        name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="details-vendor-info">
                      <h1>{name}</h1>
                      <p>{tagline}</p>
                      <div className="details-vendor-badges">
                        {(v.verified || v.partner) && <span className={`detail-badge ${badgeClass}`}>✓ {badgeText}</span>}
                        <span className="detail-badge service">{service}</span>
                      </div>
                    </div>
                  </div>
                  <div className="details-header-actions">
                    {onConnect && (
                      isLinked ? (
                        <span className="btn-requested-header">⏳ Requested</span>
                      ) : (
                        <button type="button" className="btn-connect-header" onClick={onConnect} disabled={loadingLinked}>+ Connect</button>
                      )
                    )}
                    <button type="button" className="btn-favorite">Save to Favorites</button>
                  </div>
                </div>
                <div className="details-stats-row">
                  <div className="details-stat-item">
                    <div className="details-stat-label">Rating</div>
                    <div className="details-stat-value">★ {rating > 0 ? rating.toFixed(1) : "—"}</div>
                  </div>
                  <div className="details-stat-item">
                    <div className="details-stat-label">Reviews</div>
                    <div className="details-stat-value">{count}</div>
                  </div>
                  <div className="details-stat-item">
                    <div className="details-stat-label">Response Time</div>
                    <div className="details-stat-value">&lt; 2 hrs</div>
                  </div>
                  <div className="details-stat-item">
                    <div className="details-stat-label">Completion Rate</div>
                    <div className="details-stat-value">99%</div>
                  </div>
                </div>
              </div>

              <div className="details-two-column">
                <div className="details-card">
                  <div className="details-card-title">About {name}</div>
                  <p className="details-about-text">{aboutText}</p>
                </div>
                <div className="details-card">
                  <div className="details-card-title">Performance</div>
                  <div className="performance-stars">
                    {[1, 2, 3, 4, 5].map((i) => <span key={i} className="performance-star">★</span>)}
                  </div>
                  <div className="performance-rating">{rating > 0 ? rating.toFixed(1) : "—"}★ out of 5.0</div>
                  <div className="performance-reviews">Based on {count} reviews</div>
                  <div className="performance-bars">
                    <div className="performance-bar-item">
                      <span className="performance-bar-label">Quality</span>
                      <span className="performance-bar-value">{quality.toFixed(1)} ★</span>
                    </div>
                    <div className="performance-bar-item">
                      <span className="performance-bar-label">Communication</span>
                      <span className="performance-bar-value">{communication.toFixed(1)} ★</span>
                    </div>
                    <div className="performance-bar-item">
                      <span className="performance-bar-label">Timeliness</span>
                      <span className="performance-bar-value">{timeliness.toFixed(1)} ★</span>
                    </div>
                    <div className="performance-bar-item">
                      <span className="performance-bar-label">Value</span>
                      <span className="performance-bar-value">{value.toFixed(1)} ★</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="details-card" style={{ marginBottom: 16 }}>
                <div className="details-card-title">Contact & Business Information</div>
                <div className="contact-grid">
                  <div>
                    <div className="contact-column-title">📞 Contact Information</div>
                    <a href={website.startsWith("http") ? website : `https://${website}`} className="contact-item" target="_blank" rel="noreferrer">
                      <span className="contact-item-icon">🔗</span>
                      {website.replace(/^https?:\/\//, "")}
                    </a>
                    <div className="contact-item">
                      <span className="contact-item-icon">📞</span>
                      {phone}
                    </div>
                    <a href={`mailto:${email}`} className="contact-item" style={{ textDecoration: "none", color: "inherit" }}>
                      <span className="contact-item-icon">✉️</span>
                      {email}
                    </a>
                    <div className="contact-item">
                      <span className="contact-item-icon">📍</span>
                      {location}
                    </div>
                  </div>
                  <div>
                    <div className="contact-column-title">🏢 Business Information</div>
                    <div className="contact-item">
                      <span className="contact-item-icon">💰</span>
                      {profileData?.business_details?.price_model || "Pricing: $500 - $2,000"}
                    </div>
                    <div className="contact-item">
                      <span className="contact-item-icon">⏱️</span>
                      Turnaround: 3-5 days
                    </div>
                    <div className="contact-item">
                      <span className="contact-item-icon">📊</span>
                      {profileData?.business_details?.year_of_established ? `Experience: ${new Date().getFullYear() - Number(profileData.business_details.year_of_established)}+ years` : "Experience: 20+ years"}
                    </div>
                    <div className="contact-item">
                      <span className="contact-item-icon">🌍</span>
                      Coverage: {location || "N/A"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="details-two-column">
                <div className="details-card">
                  <div className="details-card-title">Services & Specialties</div>
                  <div className="services-list">
                    <div className="service-row">
                      <span className="service-label">Primary Service</span>
                      <span className="service-value">{service}</span>
                    </div>
                    <div className="service-row">
                      <span className="service-label">Secondary Service</span>
                      <span className="service-value">{secondaryService}</span>
                    </div>
                    <div className="service-row">
                      <span className="service-label">Project Types</span>
                      <span className="service-value">{service}</span>
                    </div>
                    <div className="service-row">
                      <span className="service-label">Typical Budget</span>
                      <span className="service-value">$500</span>
                    </div>
                  </div>
                </div>
                <div className="details-card" style={{ padding: 0, overflow: "hidden" }}>
                  <div className="special-offer-header" style={{ background: "linear-gradient(135deg, #ff9800, #ff8c00)", color: "white", padding: 16, textAlign: "center" }}>
                    <div className="special-offer-heading">🏷️ Special Offers</div>
                    <div className="special-offer-count">{discounts.length} coupon{discounts.length !== 1 ? "s" : ""} available</div>
                  </div>
                  <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
                    {discounts.length > 0 ? (
                      discounts.map((d) => (
                        <div key={d._id} className="coupon-badge">
                          <div className="coupon-left">
                            <span className="coupon-value">
                              {d.type === "PERCENTAGE" ? `${d.value}% OFF` : d.type === "FIXED_AMOUNT" ? `$${d.value} OFF` : `${d.value} OFF`}
                            </span>
                            <span className="coupon-label">off</span>
                          </div>
                          <span
                            className="coupon-code"
                            title="Click to copy"
                            role="button"
                            tabIndex={0}
                            onClick={(e) => onCopyCouponCode?.(d.code, e.currentTarget)}
                            onKeyDown={(e) => e.key === "Enter" && onCopyCouponCode?.(d.code, e.currentTarget)}
                          >
                            {d.code}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="contact-item">No coupons at this time.</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="details-card">
                <div className="reviews-header">
                  <div className="details-card-title" style={{ marginBottom: 0 }}>Customer Reviews</div>
                  {onWriteReview && (
                    <button type="button" className="btn-write-review" onClick={onWriteReview}>✏️ Write Review</button>
                  )}
                </div>
                {loadingReviews && (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    <p className="mt-2 text-muted small">Loading reviews...</p>
                  </div>
                )}
                {!loadingReviews && reviewsError && <div className="alert alert-warning small">{reviewsError}</div>}
                {!loadingReviews && !reviewsError && (reviews.length > 0 ? (
                  <>
                    <div className="reviews-summary">
                      <div className="reviews-summary-left">
                        <span className="reviews-avg-rating">{rating > 0 ? rating.toFixed(1) : "—"}</span>
                        <div className="reviews-avg-info">
                          <span className="reviews-avg-stars">★★★★★</span>
                          <span className="reviews-count">{count} reviews</span>
                        </div>
                      </div>
                    </div>
                    <div className="reviews-list">
                      {reviews.map((review) => (
                        <div key={review._id} className="review-item">
                          <div className="review-item-header">
                            <div className="review-author">
                              <div className="review-author-avatar">{review.user_name?.charAt(0)?.toUpperCase() || "U"}</div>
                              <div className="review-author-info">
                                <h5>{review.user_name}</h5>
                                <span>{review.created_at ? new Date(review.created_at).toLocaleDateString("en-US") : ""}</span>
                              </div>
                            </div>
                            <div className="review-rating-stars">
                              {"★".repeat(review.rating || 0)}{"☆".repeat(5 - (review.rating || 0))}
                            </div>
                          </div>
                          <div className="review-item-title">{review.review_title}</div>
                          <div className="review-item-text">{review.review_comment}</div>
                          {review.recommend_vendor && <span className="review-recommend-badge">👍 Recommends this vendor</span>}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="no-reviews">
                    <div className="no-reviews-icon">💬</div>
                    <div className="no-reviews-title">No reviews yet</div>
                    <div className="no-reviews-text">Be the first to review this vendor</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="details-panel-footer">
          <div className="details-footer-text">Ready to work with this vendor? Get in touch today.</div>
          <div className="details-footer-actions">
            <button type="button" className="btn-close-panel" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VendorDetailsSlider;
